import {
    ChevronDown,
    Download,
    Filter,
    Plus,
    Search,
    User
} from 'lucide-react';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmployees } from '../context/EmployeeContext';
import AddEmployeeSidebar from './AddEmployeeSidebar';




const ContactsTable: React.FC = () => {
  console.log('🚀 ContactsTable component mounting/re-rendering');
  
  const navigate = useNavigate();
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('All employees');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const { employees, searchEmployees, getManagerName, getDepartments, addEmployee } = useEmployees();

  console.log('📊 ContactsTable render state:', {
    employeeCount: employees.length,
    searchTerm,
    selectedDepartment,
    showExportDropdown,
    showDepartmentDropdown,
    showAddEmployeeModal,
    selectedContactsCount: selectedContacts.length
  });


  // Apply search and department filters
  let filteredContacts = searchTerm ? searchEmployees(searchTerm) : employees;
  
  // Apply department filter
  if (selectedDepartment !== 'All employees') {
    filteredContacts = filteredContacts.filter(employee => employee.department === selectedDepartment);
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      console.log('🖱️ Click outside detected', { 
        showExportDropdown, 
        showDepartmentDropdown,
        target: e.target 
      });
      setShowExportDropdown(false);
      setShowDepartmentDropdown(false);
    };

    if (showExportDropdown || showDepartmentDropdown) {
      console.log('👂 Adding click outside listener', { showExportDropdown, showDepartmentDropdown });
      document.addEventListener('click', handleClickOutside);
      return () => {
        console.log('🚫 Removing click outside listener');
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showExportDropdown, showDepartmentDropdown]);

  // Log state changes
  useEffect(() => {
    console.log('📊 Export dropdown state changed:', showExportDropdown);
  }, [showExportDropdown]);

  useEffect(() => {
    console.log('🏢 Department dropdown state changed:', showDepartmentDropdown);
  }, [showDepartmentDropdown]);

  useEffect(() => {
    console.log('➕ Add Employee modal state changed:', showAddEmployeeModal);
  }, [showAddEmployeeModal]);

  useEffect(() => {
    console.log('🔍 Search term changed:', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    console.log('🏷️ Department filter changed:', selectedDepartment);
  }, [selectedDepartment]);

  const handleContactClick = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(contact => contact.id));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-accent text-white';
      case 'Remote': return 'bg-blue-100 text-blue-800';
      case 'In Office': return 'bg-purple-100 text-purple-800';
      case 'On Leave': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExport = (format: 'csv' | 'json') => {
    console.log('🔄 Export initiated:', { format, employeeCount: filteredContacts.length });
    
    const dataToExport = filteredContacts.map(employee => ({
      Name: employee.name,
      Email: employee.email,
      Department: employee.department,
      'Job Title': employee.jobTitle,
      Manager: getManagerName(employee.managerId),
      Status: employee.employeeStatus,
      'Hire Date': employee.hireDate,
      'Last Activity': employee.lastActivity,
      Skills: employee.skills?.join(', ') || '',
      Projects: employee.projects?.join(', ') || ''
    }));

    if (format === 'csv') {
      console.log('📊 Generating CSV export...');
      // Convert to CSV
      const headers = Object.keys(dataToExport[0] || {});
      const csvContent = [
        headers.join(','),
        ...dataToExport.map(row => 
          headers.map(header => `"${row[header as keyof typeof row] || ''}"`).join(',')
        )
      ].join('\n');

      console.log('💾 CSV content generated, length:', csvContent.length);
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const filename = `employees_${new Date().toISOString().split('T')[0]}.csv`;
      link.download = filename;
      console.log('⬇️ Triggering CSV download:', filename);
      link.click();
      console.log('✅ CSV export completed');
    } else {
      console.log('📄 Generating JSON export...');
      // Download JSON
      const jsonContent = JSON.stringify(dataToExport, null, 2);
      console.log('💾 JSON content generated, length:', jsonContent.length);
      
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const filename = `employees_${new Date().toISOString().split('T')[0]}.json`;
      link.download = filename;
      console.log('⬇️ Triggering JSON download:', filename);
      link.click();
      console.log('✅ JSON export completed');
    }
  };

  return (
    <div className="h-full bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Team Directory</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredContacts.length} employees
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <button 
                onClick={(e) => {
                  console.log('🖱️ Export button clicked', { 
                    currentState: showExportDropdown,
                    event: e.type,
                    target: e.target
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  setShowExportDropdown(!showExportDropdown);
                }}
                className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
              <Download className="h-4 w-4 icon-accent" />
              <span>Export</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        console.log('📊 CSV export option clicked');
                        e.preventDefault();
                        e.stopPropagation();
                        handleExport('csv');
                        setShowExportDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export as CSV
                    </button>
                    <button
                      onClick={(e) => {
                        console.log('📄 JSON export option clicked');
                        e.preventDefault();
                        e.stopPropagation();
                        handleExport('json');
                        setShowExportDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Export as JSON
            </button>
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={(e) => {
                console.log('➕ Add Employee button clicked', { 
                  currentModalState: showAddEmployeeModal,
                  event: e.type,
                  employeeCount: employees.length
                });
                setShowAddEmployeeModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-dark"
            >
              <Plus className="h-4 w-4" />
              <span>Add employee</span>
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 icon-accent" />
              <input
                type="text"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-80 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Department Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  console.log('🏢 Department filter button clicked', { 
                    currentState: showDepartmentDropdown,
                    selectedDepartment,
                    event: e.type
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDepartmentDropdown(!showDepartmentDropdown);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
              <Filter className="h-4 w-4 icon-accent" />
                <span>{selectedDepartment}</span>
              <ChevronDown className="h-4 w-4" />
            </button>

              {showDepartmentDropdown && (
                <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        console.log('🏢 "All employees" filter selected');
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedDepartment('All employees');
                        setShowDepartmentDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        selectedDepartment === 'All employees' ? 'bg-teal-50 text-teal-600' : 'text-gray-700'
                      }`}
                    >
                      All employees
                    </button>
                    {getDepartments().map((department) => (
                      <button
                        key={department}
                        onClick={(e) => {
                          console.log('🏢 Department filter selected:', department);
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedDepartment(department);
                          setShowDepartmentDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          selectedDepartment === department ? 'bg-teal-50 text-teal-600' : 'text-gray-700'
                        }`}
                      >
                        {department}
            </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>

          <div className="flex items-center space-x-2">
            {selectedContacts.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedContacts.length} selected
                </span>
                <button 
                  onClick={() => setSelectedContacts([])}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Activity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manager
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredContacts.map((contact) => (
              <tr
                key={contact.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => handleContactClick(contact.id)}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectContact(contact.id);
                    }}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-sm">
                        {contact.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                      {contact.jobTitle && (
                        <div className="text-sm text-gray-500">{contact.jobTitle}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{contact.email}</td>

                <td className="px-6 py-4 text-sm text-gray-900">{contact.department}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.employeeStatus)}`}>
                    {contact.employeeStatus}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{contact.lastActivity || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{getManagerName(contact.managerId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {filteredContacts.length === 0 && searchTerm && (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or filter to find what you're looking for.
          </p>
        </div>
      )}

      {/* Add Employee Sidebar */}
      <AddEmployeeSidebar
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onAdd={addEmployee}
      />
    </div>
  );
};

export default ContactsTable;
